import { appConfig } from '@/core/config';

/**
 * Environment-aware API configuration
 * Provides API base URLs and portal URLs based on current environment
 * Supports runtime configuration override via runtime-config.json
 */
class EnvironmentConfig {
  private static instance: EnvironmentConfig;

  private constructor() { }

  static getInstance(): EnvironmentConfig {
    if (!EnvironmentConfig.instance) {
      EnvironmentConfig.instance = new EnvironmentConfig();
    }
    return EnvironmentConfig.instance;
  }

  /**
   * Get API base URLs for current environment
   * 
   * Priority:
   * 1. For local dev (vite dev server): Use VITE_ENV-based config
   * 2. For production builds: Use runtime-config.json if available
   * 3. Fallback: Use build-time config based on VITE_ENV
   */
  getApiUrls() {
    const buildEnv = appConfig.getEnvironment();

    // When running on vite dev server (import.meta.env.DEV), always use VITE_ENV config
    // This allows `yarn dev`, `yarn qa`, `yarn prod` to work correctly
    const isViteDevServer = import.meta.env.DEV;

    // Dev-only: allow overriding the environment via localStorage
    const devEnvOverride = isViteDevServer ? localStorage.getItem('dev_env_override') as 'qa' | 'prod' | null : null;
    const env = devEnvOverride ?? buildEnv;
    
    // Use build-time config based on VITE_ENV
    const urls = {
      dev: {
        auth: 'https://qa-cortex.nayax.com/users/v1', // Always use QA Cortex for sign-in
        office: 'https://qa2-evbackoffice.nayax.com/api/v1',
        accounts: 'https://qa2-evbackoffice.nayax.com/accounts/api/v1',
        kiosk: 'https://qa2-evbackoffice.nayax.com/kiosk/v1',
        ocpiCpo: 'https://qa2-evbackoffice.nayax.com/ocpi-cpo',
        ocpiAdmin: 'https://qa2-evbackoffice.nayax.com/ocpi-admin',
        s3BaseUrl: 'https://s3.us-east-1.amazonaws.com'
      },
      qa: {
        auth: 'https://qa-cortex.nayax.com/users/v1',
        office: 'https://qa2-evbackoffice.nayax.com/api/v1',
        accounts: 'https://qa2-evbackoffice.nayax.com/accounts/api/v1',
        kiosk: 'https://qa2-evbackoffice.nayax.com/kiosk/v1',
        ocpiCpo: 'https://qa2-evbackoffice.nayax.com/ocpi-cpo',
        ocpiAdmin: 'https://qa2-evbackoffice.nayax.com/ocpi-admin',
        s3BaseUrl: 'https://s3.us-east-1.amazonaws.com'
      },
      prod: {
        auth: 'https://cortex.nayax.com/users/v1',
        office: 'https://ev-backoffice.nayax.com/api/v1',
        accounts: 'https://ev-backoffice.nayax.com/accounts/api/v1',
        kiosk: 'https://ev-backoffice.nayax.com/kiosk/v1',
        ocpiCpo: 'https://ev-backoffice.nayax.com/ocpi-cpo',
        ocpiAdmin: 'https://ev-backoffice.nayax.com/ocpi-admin',
        s3BaseUrl: ''
      },
      // Local development: back-office runs on localhost:9218 (see launchSettings.json)
      local: {
        auth: 'https://qa-cortex.nayax.com/users/v1',
        office: 'http://localhost:9218/api/v1',
        accounts: 'http://localhost:9218/accounts/api/v1',
        kiosk: 'http://localhost:9218/kiosk/v1',
        ocpiCpo: 'http://localhost:9218/ocpi-cpo',
        ocpiAdmin: 'http://localhost:9218/ocpi-admin',
        s3BaseUrl: 'https://s3.us-east-1.amazonaws.com'
      },
    };

    const defaults = urls[env];

    console.log(`🌐 Resolving API URLs - env: ${env}, isViteDevServer: ${isViteDevServer}`);

    // Try runtime config only if not running on vite dev server
    // Merge with defaults so missing keys fall back to build-time config
    if (!isViteDevServer) {
      const runtimeConfig = appConfig.getRuntimeConfig();
      if (runtimeConfig?.apiUrls) {
        const merged = {
          ...defaults,
          ...runtimeConfig.apiUrls
        };
        console.log('✅ Using runtime config URLs:', merged);
        return merged;
      }
    }

    console.log('⚙️ Using build-time config URLs:', defaults);
    return defaults;
  }

  /**
   * Get portal URL for external machine links
   * 
   * Priority:
   * 1. For local dev (vite dev server): Use VITE_ENV-based config
   * 2. For production builds: Use runtime-config.json if available
   * 3. Fallback: Use build-time config based on VITE_ENV
   */
  getPortalUrl(): string {
    const buildEnv = appConfig.getEnvironment();

    // When running on vite dev server (import.meta.env.DEV), always use VITE_ENV config
    const isViteDevServer = import.meta.env.DEV;
    const devEnvOverride = isViteDevServer ? localStorage.getItem('dev_env_override') as 'qa' | 'prod' | null : null;
    const env = devEnvOverride ?? buildEnv;
    
    // Try runtime config only if not running on vite dev server
    if (!isViteDevServer) {
      const runtimeConfig = appConfig.getRuntimeConfig();
      if (runtimeConfig?.portalUrl) {
        return runtimeConfig.portalUrl;
      }
    }

    // Use build-time config based on VITE_ENV
    const portalUrls = {
      dev: 'https://dev.nayax.com',
      qa: 'https://qa.nayax.com',
      prod: 'https://my.nayax.com',
      local: 'https://qa.nayax.com',
    };

    return portalUrls[env];
  }
}

// Export singleton instance
export const environmentConfig = EnvironmentConfig.getInstance();


// Keep legacy constants for backward compatibility (will be initialized after runtime config loads)
let apiUrls = environmentConfig.getApiUrls();
export let BASE_AUTH_API = apiUrls.auth;
export let BASE_AUTH_OFFICE_API = apiUrls.office;
export let BASE_AUTH_OFFICE_API_V2 = apiUrls.office.replace('/api/v1', '/api/v2');
export let BASE_ACCOUNTS_API = apiUrls.accounts;
export let BASE_KIOSK_API = apiUrls.kiosk;
export let BASE_OCPI_CPO_API = apiUrls.ocpiCpo ?? '';
export let BASE_OCPI_ADMIN_API = apiUrls.ocpiAdmin ?? '';
export let BASE_S3_URL = apiUrls.s3BaseUrl ?? '';

/**
 * Update legacy constants after runtime config is loaded
 * This should be called from main.tsx after config initialization
 */
export const updateApiConstants = () => {
  console.log('🔄 Updating API constants...');

  apiUrls = environmentConfig.getApiUrls();
  BASE_AUTH_API = apiUrls.auth;
  BASE_AUTH_OFFICE_API = apiUrls.office;
  BASE_AUTH_OFFICE_API_V2 = apiUrls.office.replace('/api/v1', '/api/v2');
  BASE_ACCOUNTS_API = apiUrls.accounts;
  BASE_KIOSK_API = apiUrls.kiosk;
  BASE_OCPI_CPO_API = apiUrls.ocpiCpo ?? '';
  BASE_OCPI_ADMIN_API = apiUrls.ocpiAdmin ?? '';
  BASE_S3_URL = apiUrls.s3BaseUrl ?? '';

  console.log('✅ API constants updated:', {
    BASE_AUTH_API,
    BASE_AUTH_OFFICE_API,
    BASE_KIOSK_API,
    BASE_S3_URL,
  });
};